import { normalizeVerbatimLink } from './VerbatimUtils';

const VALID_BLOCK_TYPES = ['text', 'image'];

const isPlainObject = (value) => (
	!!value && typeof value === 'object' && !Array.isArray(value)
);

const cloneSafe = (value, fallback) => {
	if (value === undefined || value === null) return fallback;
	return JSON.parse(JSON.stringify(value));
};

const isNonEmptyString = (value) => typeof value === 'string' && value.trim() !== '';

const toArray = (value) => {
	if (Array.isArray(value)) return value;
	if (isPlainObject(value)) return [value];
	return [];
};

const getRichTextValue = (value) => {
	if (!Array.isArray(value?.richText)) return '';

	return value.richText
		.filter((part) => typeof part?.text === 'string')
		.map((part) => part.text)
		.join('');
};

const createIssue = ({
	severity,
	scope = 'verbatim',
	verbatimId,
	verbatimIndex,
	sourceRow,
	sourceType,
	field,
	message,
	resolution,
}) => ({
	severity,
	scope,
	verbatimId,
	verbatimIndex,
	sourceRow,
	sourceType,
	field,
	message,
	...(resolution ? { resolution } : {}),
});

const normalizeContent = (content) => {
	if (typeof content === 'string') {
		return {
			value: content,
			isEmpty: content.trim() === '',
			wasCorrected: false,
		};
	}

	const richTextValue = getRichTextValue(content);
	if (richTextValue.trim()) {
		return {
			value: richTextValue,
			isEmpty: false,
			wasCorrected: true,
		};
	}

	if (!Array.isArray(content)) {
		return {
			value: '',
			isEmpty: true,
			wasCorrected: content !== undefined && content !== null,
		};
	}

	const usedTypes = new Set();
	let wasCorrected = false;
	const normalizedBlocks = [];

	content.forEach((block) => {
		if (!isPlainObject(block)) {
			wasCorrected = true;
			return;
		}

		const blockType = typeof block.type === 'string' ? block.type.trim().toLowerCase() : '';
		const blockContent = typeof block.content === 'string' ? block.content : '';

		if (
			!VALID_BLOCK_TYPES.includes(blockType)
			|| blockContent.trim() === ''
			|| usedTypes.has(blockType)
		) {
			wasCorrected = true;
			return;
		}

		if (block.type !== blockType || Object.keys(block).some((key) => !['type', 'content'].includes(key))) {
			wasCorrected = true;
		}

		usedTypes.add(blockType);
		normalizedBlocks.push({ type: blockType, content: blockContent });
	});

	return {
		value: normalizedBlocks,
		isEmpty: normalizedBlocks.length === 0,
		wasCorrected,
	};
};

const normalizeVerbatimLabels = (labelEntries) => {
	if (!isPlainObject(labelEntries)) return {};

	return Object.entries(labelEntries).reduce((acc, [labelType, tags]) => {
		const cleanedLabelType = typeof labelType === 'string' ? labelType.trim() : '';
		if (!cleanedLabelType) return acc;

		const normalizedTags = toArray(tags).reduce((tagAcc, tag) => {
			if (!isPlainObject(tag)) return tagAcc;

			const name = isNonEmptyString(tag.name) ? tag.name.trim() : '';
			if (!name) return tagAcc;

			const normalizedTag = {
				name,
				state: isNonEmptyString(tag.state) ? tag.state.trim() : 'active',
				type: isNonEmptyString(tag.type) ? tag.type.trim() : cleanedLabelType,
			};

			const alreadyExists = tagAcc.some((currentTag) => (
				currentTag.name === normalizedTag.name
				&& currentTag.state === normalizedTag.state
				&& currentTag.type === normalizedTag.type
			));

			if (!alreadyExists) {
				tagAcc.push(normalizedTag);
			}

			return tagAcc;
		}, []);

		acc[cleanedLabelType] = normalizedTags;
		return acc;
	}, {});
};

const normalizeRootLabels = (labels) => {
	if (!isPlainObject(labels)) return {};

	return Object.entries(labels).reduce((acc, [labelType, definitions]) => {
		const cleanedLabelType = typeof labelType === 'string' ? labelType.trim() : '';
		if (!cleanedLabelType) return acc;

		const normalizedDefinitions = toArray(definitions).reduce((definitionAcc, definition) => {
			if (!isPlainObject(definition)) return definitionAcc;

			const name = isNonEmptyString(definition.name) ? definition.name.trim() : '';
			if (!name || definitionAcc.some((currentDefinition) => currentDefinition.name === name)) {
				return definitionAcc;
			}

			definitionAcc.push({
				name,
				keywords: Array.isArray(definition.keywords)
					? definition.keywords.filter(isNonEmptyString).map((keyword) => keyword.trim())
					: [],
				explanation: isNonEmptyString(definition.explanation) ? definition.explanation.trim() : '',
				mandatory: typeof definition.mandatory === 'boolean' ? definition.mandatory : false,
				sentimentEnabled: typeof definition.sentimentEnabled === 'boolean'
					? definition.sentimentEnabled
					: false,
			});

			return definitionAcc;
		}, []);

		acc[cleanedLabelType] = normalizedDefinitions;
		return acc;
	}, {});
};

const normalizeLabelTypes = (labelTypes) => {
	if (!isPlainObject(labelTypes)) return {};

	return Object.entries(labelTypes).reduce((acc, [labelType, options]) => {
		const cleanedLabelType = typeof labelType === 'string' ? labelType.trim() : '';
		if (!cleanedLabelType) return acc;

		const normalizedOptions = isPlainObject(options) ? options : {};
		acc[cleanedLabelType] = {
			mandatory: typeof normalizedOptions.mandatory === 'boolean' ? normalizedOptions.mandatory : false,
			sentimentEnabled: typeof normalizedOptions.sentimentEnabled === 'boolean'
				? normalizedOptions.sentimentEnabled
				: false,
			multiValue: typeof normalizedOptions.multiValue === 'boolean' ? normalizedOptions.multiValue : false,
		};

		return acc;
	}, {});
};

const normalizeNotes = (notes) => (Array.isArray(notes) ? notes : []);

const normalizeSlides = (slides) => (Array.isArray(slides) ? slides : []);

const validateVerbatimRecord = (verbatim, index, options) => {
	const normalizedVerbatim = isPlainObject(verbatim) ? cloneSafe(verbatim, {}) : {};
	const issues = [];
	const sourceRow = options.sourceRows?.[index] ?? index + 1;
	const sourceType = options.sourceType || 'item';
	const parsedId = Number(normalizedVerbatim.id);

	if (Number.isInteger(parsedId) && parsedId > 0) {
		normalizedVerbatim.id = parsedId;
		if (normalizedVerbatim.id !== verbatim?.id) {
			issues.push(createIssue({
				severity: 'warning',
				verbatimId: parsedId,
				verbatimIndex: index,
				sourceRow,
				sourceType,
				field: 'id',
				resolution: 'Corrected automatically',
				message: 'The ID was converted to a number automatically.',
			}));
		}
	} else {
		normalizedVerbatim.id = index + 1;
		issues.push(createIssue({
			severity: 'warning',
			verbatimId: normalizedVerbatim.id,
			verbatimIndex: index,
			sourceRow,
			sourceType,
			field: 'id',
			resolution: 'Corrected automatically',
			message: 'The ID was converted to a number automatically.',
		}));
	}

	if (Object.prototype.hasOwnProperty.call(normalizedVerbatim, 'translation')) {
		normalizedVerbatim.translation = typeof normalizedVerbatim.translation === 'string'
			? normalizedVerbatim.translation
			: '';
	}

	const rawLink = normalizedVerbatim.link;
	const normalizedLink = normalizeVerbatimLink(rawLink).trim();
	normalizedVerbatim.link = normalizedLink;

	if (typeof rawLink !== 'string' && normalizedLink) {
		issues.push(createIssue({
			severity: 'warning',
			verbatimId: normalizedVerbatim.id,
			verbatimIndex: index,
			sourceRow,
			sourceType,
			field: 'link',
			resolution: 'Corrected automatically',
			message: 'The link was corrected automatically.',
		}));
	}

	const normalizedContent = normalizeContent(normalizedVerbatim.verbatim);
	normalizedVerbatim.verbatim = normalizedContent.value;

	if (normalizedContent.wasCorrected) {
		issues.push(createIssue({
			severity: 'warning',
			verbatimId: normalizedVerbatim.id,
			verbatimIndex: index,
			sourceRow,
			sourceType,
			field: 'verbatim',
			resolution: 'Corrected automatically',
			message: 'The verbatim was corrected automatically.',
		}));
	}

	const statusMessages = [];

	if (!normalizedLink) {
		const message = 'The link is missing and must be added.';
		statusMessages.push(message);
		issues.push(createIssue({
			severity: 'error',
			verbatimId: normalizedVerbatim.id,
			verbatimIndex: index,
			sourceRow,
			sourceType,
			field: 'link',
			message,
		}));
	}

	if (normalizedContent.isEmpty) {
		const message = 'The verbatim content is missing and must be added.';
		statusMessages.push(message);
		issues.push(createIssue({
			severity: 'error',
			verbatimId: normalizedVerbatim.id,
			verbatimIndex: index,
			sourceRow,
			sourceType,
			field: 'verbatim',
			message,
		}));
	}

	normalizedVerbatim.labels = normalizeVerbatimLabels(normalizedVerbatim.labels);

	if (statusMessages.length > 0) {
		normalizedVerbatim.status = 'error';
		normalizedVerbatim.statusMessage = statusMessages.join(' ');
	} else {
		delete normalizedVerbatim.status;
		delete normalizedVerbatim.statusMessage;
	}

	return {
		normalizedVerbatim,
		issues,
	};
};

export const validateImportedProjectStructure = (data, options = {}) => {
	const inputIsObject = isPlainObject(data);
	const normalizedData = inputIsObject ? cloneSafe(data, {}) : {};
	const projectIssues = [];

	if (!inputIsObject || !Array.isArray(normalizedData.verbatims)) {
		projectIssues.push(createIssue({
			severity: 'warning',
			scope: 'project',
			field: 'verbatims',
			resolution: 'Corrected automatically',
			message: 'The verbatims list was corrected automatically.',
		}));
	}

	const normalizedVerbatims = Array.isArray(normalizedData.verbatims)
		? normalizedData.verbatims.map((verbatim, index) => validateVerbatimRecord(verbatim, index, options))
		: [];

	normalizedData.verbatims = normalizedVerbatims.map(({ normalizedVerbatim }) => normalizedVerbatim);
	normalizedData.labels = normalizeRootLabels(normalizedData.labels);
	normalizedData.labelTypes = normalizeLabelTypes(normalizedData.labelTypes);
	normalizedData.notes = normalizeNotes(normalizedData.notes);
	normalizedData.slides = normalizeSlides(normalizedData.slides);

	const issues = [
		...projectIssues,
		...normalizedVerbatims.flatMap(({ issues: verbatimIssues }) => verbatimIssues),
	];
	const warnings = issues.filter((issue) => issue.severity === 'warning');
	const errors = issues.filter((issue) => issue.severity === 'error');
	const defectiveVerbatimIndexes = [...new Set(
		errors
			.filter((issue) => issue.scope === 'verbatim' && issue.verbatimIndex !== undefined)
			.map((issue) => issue.verbatimIndex)
	)];
	const defectiveVerbatimIds = [...new Set(
		errors
			.filter((issue) => issue.scope === 'verbatim' && issue.verbatimId !== undefined)
			.map((issue) => issue.verbatimId)
	)];

	return {
		normalizedData,
		warnings,
		errors,
		defectiveVerbatimIndexes,
		defectiveVerbatimIds,
		verbatimCount: normalizedData.verbatims.length,
	};
};

export const stripDefectiveVerbatims = (data, defectiveVerbatimIndexes = []) => {
	const defectiveIndexes = new Set(defectiveVerbatimIndexes);
	const clonedData = cloneSafe(data, {});
	clonedData.verbatims = Array.isArray(clonedData.verbatims)
		? clonedData.verbatims.filter((verbatim, index) => !defectiveIndexes.has(index))
		: [];
	return clonedData;
};
